import { productPhotoMetadata } from '@iheartjane/menu';
import {
  AddIcon,
  Button,
  Flex,
  Image,
  List,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import capitalize from 'lodash-es/capitalize';

import type { QuickCart } from '@jane/shared-ecomm/data-access';
import { formatTime } from '@jane/shared/util';

import styles from './MenuQuickCheckoutRow.module.css';

interface PreviousOrderCardProps {
  onAddToCartClick: (params: {
    count: number;
    priceId: PriceId;
    productId: number;
  }) => void;
  onBuyAgainClick: () => void;
  quickCart: QuickCart & { id: number };
  reservationPath: string;
}

export const PreviousOrderCard = ({
  onAddToCartClick,
  onBuyAgainClick,
  quickCart,
  reservationPath,
}: PreviousOrderCardProps) => {
  const isMobile = useMobileMediaQuery({});
  const productCount = quickCart.products.length;
  const productsToShow = quickCart.products.slice(0, isMobile ? 2 : 3);
  const formattedDate = formatTime({
    time: quickCart.date,
    outputFormat: 'MMMM dd, yyyy',
  });

  return (
    <Flex
      flexDirection="column"
      gap={16}
      className={styles['previousOrderContainer']}
      border="grays-ultralight"
      borderRadius="lg"
      height="100%"
      style={{ transition: 'transform 160ms ease' }}
    >
      <Flex justifyContent="space-between" width="100%">
        <Typography color="grays-mid" variant="small-body-regular">
          {formattedDate}
        </Typography>
        <Typography color="grays-mid" variant="small-body-regular">
          {pluralize({
            noun: 'item',
            number: productCount,
            returnValue: 'both',
          })}
        </Typography>
      </Flex>
      <List label={`products in order ${quickCart.id}`}>
        {productsToShow.map((product, i) => {
          const { url } = productPhotoMetadata(product);
          return (
            <List.Item key={`${i}-${product.product_id}`}>
              <Flex width="100%" gap={8}>
                <Image
                  src={url}
                  altText={product.name}
                  border
                  borderRadius="8px"
                  height="48px"
                  width="48px"
                  responsive
                  sizes="thumbnail"
                />
                <Flex flexDirection="column">
                  <Typography variant="small-body-semibold" color="emerald">
                    {capitalize(product.type)}
                  </Typography>
                  <Typography variant="body-semibold">
                    {product.name}
                  </Typography>
                  <Typography variant="small-body-regular" color="grays-mid">
                    {product.brand}
                  </Typography>
                </Flex>

                {product.is_available ? (
                  <Button
                    variant="tertiary"
                    size="small"
                    ml="auto"
                    style={{ padding: '8px' }}
                    aria-label={`Add ${product.name} to cart`}
                    label={<AddIcon size="sm" />}
                    onClick={() =>
                      onAddToCartClick({
                        productId: product.product_id,
                        priceId: product.price_id,
                        count: product.count,
                      })
                    }
                  />
                ) : (
                  <Flex
                    background="grays-ultralight"
                    borderRadius="sm"
                    alignSelf="flex-start"
                    minWidth="97px"
                    ml="auto"
                    px={12}
                    py={4}
                  >
                    <Typography variant="small-body-semibold" color="grays-mid">
                      Out of stock
                    </Typography>
                  </Flex>
                )}
              </Flex>
            </List.Item>
          );
        })}
      </List>
      <Flex
        className={styles['previousOrderButtons']}
        width="100%"
        gap={16}
        mt="auto"
      >
        <Button
          variant="primary-inverse"
          label={
            productCount === 1 ? 'View item' : `View all ${productCount} items`
          }
          aria-label={`View details for order ${quickCart.id}`}
          to={reservationPath}
          full
        />
        <Button
          variant="secondary"
          label="Buy again"
          aria-label={`Buy order ${quickCart.id} again`}
          onClick={onBuyAgainClick}
          full
        />
      </Flex>
    </Flex>
  );
};
